/**
 * Illustrative sample data for the Emotion Profile Tracking demo.
 * Not derived from any real person -- these are hand-authored patterns
 * meant to demonstrate the kind of longitudinal insight the real product
 * (built from voice, expression, and biosignal check-ins over weeks) would
 * surface. Shares the same 7-label affect vocabulary as the rest of the
 * site (see affectFusion.js) so the visuals read consistently.
 */
export const EMOTION_COLORS = {
    anger: "#ff6d6d",
    happy: "#58f09a",
    neutral: "#a9b5c8",
    sad: "#7d91ff",
    fear: "#ff9b63",
    surprise: "#ffe066",
    disgust: "#d899ff",
};

export const LABEL_COPY = {
    anger: "Frustration",
    disgust: "Distrust",
    fear: "Anxiety",
    happy: "Positive",
    neutral: "Neutral",
    sad: "Low mood",
    surprise: "Surprise",
};

export const PROFILE_SAMPLES = [
    {
        id: "busy-professional",
        icon: "💼",
        label: "Busy Professional",
        description: "Weekday workload stress that builds toward deadlines and resets on weekends.",
        insight: "Frustration consistently peaks Tuesday–Thursday and drops sharply by the weekend — a workload pattern, not a mood problem.",
        days: [
            { day: "Mon", emotion: "neutral", intensity: 0.35 },
            { day: "Tue", emotion: "anger", intensity: 0.55 },
            { day: "Wed", emotion: "anger", intensity: 0.68 },
            { day: "Thu", emotion: "anger", intensity: 0.6 },
            { day: "Fri", emotion: "neutral", intensity: 0.4 },
            { day: "Sat", emotion: "happy", intensity: 0.5 },
            { day: "Sun", emotion: "happy", intensity: 0.45 },
            { day: "Mon", emotion: "neutral", intensity: 0.38 },
            { day: "Tue", emotion: "anger", intensity: 0.5 },
            { day: "Wed", emotion: "anger", intensity: 0.7 },
            { day: "Thu", emotion: "fear", intensity: 0.5 },
            { day: "Fri", emotion: "neutral", intensity: 0.42 },
            { day: "Sat", emotion: "happy", intensity: 0.55 },
            { day: "Sun", emotion: "happy", intensity: 0.5 },
        ],
    },
    {
        id: "new-parent",
        icon: "🌙",
        label: "New Parent",
        description: "Sleep-driven mood swings with a slow, steady recovery.",
        insight: "Low-mood days cluster around disrupted sleep in week one, but the trend improves steadily as sleep stretches lengthen in week two.",
        days: [
            { day: "Wk1·D1", emotion: "sad", intensity: 0.6 },
            { day: "Wk1·D2", emotion: "sad", intensity: 0.65 },
            { day: "Wk1·D3", emotion: "neutral", intensity: 0.4 },
            { day: "Wk1·D4", emotion: "sad", intensity: 0.55 },
            { day: "Wk1·D5", emotion: "neutral", intensity: 0.42 },
            { day: "Wk1·D6", emotion: "happy", intensity: 0.4 },
            { day: "Wk1·D7", emotion: "sad", intensity: 0.45 },
            { day: "Wk2·D1", emotion: "neutral", intensity: 0.4 },
            { day: "Wk2·D2", emotion: "happy", intensity: 0.45 },
            { day: "Wk2·D3", emotion: "neutral", intensity: 0.35 },
            { day: "Wk2·D4", emotion: "happy", intensity: 0.5 },
            { day: "Wk2·D5", emotion: "happy", intensity: 0.55 },
            { day: "Wk2·D6", emotion: "happy", intensity: 0.6 },
            { day: "Wk2·D7", emotion: "happy", intensity: 0.58 },
        ],
    },
    {
        id: "shift-worker",
        icon: "🌗",
        label: "Shift Worker",
        description: "Energy and mood tracking an irregular schedule, not the calendar.",
        insight: "Calm and low-mood days track shift rotation, not day of week — a scheduling signal a traditional weekly check-in would miss entirely.",
        days: [
            { day: "D1", emotion: "neutral", intensity: 0.4 },
            { day: "D2", emotion: "sad", intensity: 0.5 },
            { day: "D3", emotion: "sad", intensity: 0.58 },
            { day: "D4", emotion: "happy", intensity: 0.5 },
            { day: "D5", emotion: "happy", intensity: 0.48 },
            { day: "D6", emotion: "neutral", intensity: 0.38 },
            { day: "D7", emotion: "sad", intensity: 0.52 },
            { day: "D8", emotion: "sad", intensity: 0.6 },
            { day: "D9", emotion: "neutral", intensity: 0.4 },
            { day: "D10", emotion: "happy", intensity: 0.5 },
            { day: "D11", emotion: "happy", intensity: 0.55 },
            { day: "D12", emotion: "neutral", intensity: 0.36 },
            { day: "D13", emotion: "sad", intensity: 0.5 },
            { day: "D14", emotion: "happy", intensity: 0.46 },
        ],
    },
    {
        id: "student-exams",
        icon: "📚",
        label: "Student During Exams",
        description: "Anxiety climbing into exam week, releasing right after.",
        insight: "Anxiety builds for four straight days before an exam, then drops within a day of it ending — a predictable, situational pattern rather than a lasting one.",
        days: [
            { day: "Mon", emotion: "neutral", intensity: 0.35 },
            { day: "Tue", emotion: "neutral", intensity: 0.38 },
            { day: "Wed", emotion: "happy", intensity: 0.45 },
            { day: "Thu", emotion: "neutral", intensity: 0.4 },
            { day: "Fri", emotion: "neutral", intensity: 0.42 },
            { day: "Sat", emotion: "happy", intensity: 0.5 },
            { day: "Sun", emotion: "fear", intensity: 0.45 },
            { day: "Mon", emotion: "fear", intensity: 0.6 },
            { day: "Tue", emotion: "fear", intensity: 0.7 },
            { day: "Wed", emotion: "fear", intensity: 0.78 },
            { day: "Thu · Exam", emotion: "fear", intensity: 0.8 },
            { day: "Fri", emotion: "happy", intensity: 0.55 },
            { day: "Sat", emotion: "happy", intensity: 0.5 },
            { day: "Sun", emotion: "neutral", intensity: 0.38 },
        ],
    },
];
